import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer, IconButton, Box, Typography, Divider, List, ListItemButton,
  ListItemText, TextField, InputAdornment, Button, Tooltip, useTheme, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useLocation } from "react-router-dom";
import HelpMarkdown from "./HelpMarkdown";
import { tokens } from "../theme";
import "../markdown.css";

/**
 * The in-app manual.
 *
 * Content is built from docs/manual/src/*.md into public/help/ (see
 * docs/manual/build.js) — never edit public/help by hand, it is generated.
 *
 * Opens on the chapter matching the current route, with the full contents list
 * one tap away. Pass `chapter` to force a specific slug (e.g. the "?" next to
 * the prediction controls opens the Predictions chapter directly).
 */

const HELP_BASE = (process.env.PUBLIC_URL || "") + "/help";

/** Longest matching route wins, so /running/analysis beats /running. */
function chapterForRoute(chapters, pathname) {
  let best = null;
  let bestLen = -1;
  for (const ch of chapters) {
    for (const r of ch.routes || []) {
      if (pathname === r || pathname.startsWith(r + "/") || pathname.startsWith(r)) {
        if (r.length > bestLen) { best = ch; bestLen = r.length; }
      }
    }
  }
  return best;
}

const HelpDrawer = ({ open, onClose, chapter: forcedChapter, content: legacyContent }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const scrollRef = useRef(null);

  const [index, setIndex] = useState(null);
  const [slug, setSlug] = useState(null);
  const [bodies, setBodies] = useState({});     // slug -> markdown
  const [showContents, setShowContents] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);

  // Load the chapter index once the drawer is first opened.
  useEffect(() => {
    if (!open || index) return;
    fetch(`${HELP_BASE}/index.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("index"))))
      .then(setIndex)
      .catch(() => setError("Could not load the manual."));
  }, [open, index]);

  // Pick the chapter for this route each time the drawer opens.
  useEffect(() => {
    if (!open || !index) return;
    const target =
      (forcedChapter && index.chapters.find((c) => c.slug === forcedChapter)) ||
      chapterForRoute(index.chapters, location.pathname) ||
      index.chapters[0];
    setSlug(target ? target.slug : null);
    setShowContents(false);
    setQuery("");
  }, [open, index, forcedChapter, location.pathname]);

  // Fetch chapter bodies on demand, then keep them.
  useEffect(() => {
    if (!slug || bodies[slug]) return;
    fetch(`${HELP_BASE}/${slug}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(slug))))
      .then((md) => setBodies((prev) => ({ ...prev, [slug]: md })))
      .catch(() => setError("Could not load that chapter."));
  }, [slug, bodies]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo(0, 0);
  }, [slug, showContents]);

  // Search needs every chapter loaded; pull the rest in on first search.
  useEffect(() => {
    if (!query || !index) return;
    for (const c of index.chapters) {
      if (bodies[c.slug]) continue;
      fetch(`${HELP_BASE}/${c.slug}.md`)
        .then((r) => (r.ok ? r.text() : ""))
        .then((md) => md && setBodies((prev) => ({ ...prev, [c.slug]: md })))
        .catch(() => {});
    }
  }, [query, index, bodies]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || !index) return null;
    const out = [];
    for (const c of index.chapters) {
      const md = bodies[c.slug];
      if (!md) continue;
      const at = md.toLowerCase().indexOf(q);
      if (at === -1) continue;
      const start = Math.max(0, at - 60);
      out.push({
        slug: c.slug,
        title: c.title,
        snippet: (start > 0 ? "…" : "") + md.slice(start, at + 120).replace(/[#*>`]/g, "") + "…",
      });
    }
    return out;
  }, [query, index, bodies]);

  const current = index?.chapters.find((c) => c.slug === slug);
  const chapterSlugs = useMemo(() => (index?.chapters || []).map((c) => c.slug), [index]);
  const markdown = legacyContent != null ? legacyContent : bodies[slug];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: isMobile ? "100%" : "75%", backgroundColor: colors.grey[800] } }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {/* header */}
        <Box display="flex" alignItems="center" gap={1} px={2} py={1.5}>
          {!showContents && index && (
            <Tooltip title="All chapters">
              <IconButton size="small" onClick={() => setShowContents(true)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }} noWrap>
            {showContents ? "Manual" : current?.title || "Help"}
          </Typography>
          <Tooltip title="Download the full manual (PDF)">
            <span>
              <IconButton size="small" component="a" href={`${HELP_BASE}/mkpace-manual.pdf`} target="_blank" rel="noopener">
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        {index && (
          <Box px={2} pb={1}>
            <TextField
              size="small" fullWidth placeholder="Search the manual"
              value={query} onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Box>
        )}
        <Divider />

        <Box ref={scrollRef} sx={{ px: 3, py: 2, flexGrow: 1, overflowY: "auto" }} className="markdown-body">
          {error && <Typography color="error">{error}</Typography>}

          {results ? (
            results.length ? (
              <List dense>
                {results.map((r) => (
                  <ListItemButton key={r.slug} onClick={() => { setSlug(r.slug); setQuery(""); }}>
                    <ListItemText primary={r.title} secondary={r.snippet} />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography color={colors.grey[300]}>No matches.</Typography>
            )
          ) : showContents && index ? (
            <List>
              {index.chapters.map((c) => (
                <ListItemButton key={c.slug} selected={c.slug === slug} onClick={() => { setSlug(c.slug); setShowContents(false); }}>
                  <ListItemText primary={c.title} />
                </ListItemButton>
              ))}
            </List>
          ) : markdown ? (
            <HelpMarkdown chapterSlugs={chapterSlugs} onChapterLink={setSlug}>{markdown}</HelpMarkdown>
          ) : (
            !error && <Typography color={colors.grey[300]}>Loading…</Typography>
          )}
        </Box>

        {!showContents && index && (
          <>
            <Divider />
            <Box p={1.5} display="flex" justifyContent="center">
              <Button size="small" onClick={() => setShowContents(true)} sx={{ textTransform: "none", color: colors.grey[300] }}>
                All chapters
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default HelpDrawer;
